/* eslint-disable @typescript-eslint/member-ordering */
import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { Shift } from "@enums/shift.enum";
import { AvatarForUserProfile } from "@helpers/avatarHelper";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { buildOrgChart } from "@helpers/orgChartHelper";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { OrgChartNode } from "@interfaces/orgChartNode.interface";
import { Site } from "@interfaces/site.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ExportLogService } from "@services/exportLog.service";
import { ProtectedGridService } from "@services/protectedGrid.service";
import { SiteService } from "@services/site.service";
import { StaffAttendanceService } from "@services/staffAttendance.service";
import { TrainingService } from "@services/trainingService.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "orgChart",
	styleUrls: ["orgChart.css"],
	templateUrl: "orgChart.html"
})
export class OrgChartAdminComponent {
	canExportGrid = false;
	deleteEnabled = false;
	editEnabled = true;
	expandedNodes: Set<number> = new Set<number>();
	filteredRoots: OrgChartNode[] = [];
	gridLoading = false;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	isSuperAdmin = false;
	itemType = "User"
	itemUrl: string = location.href.split("/")[3] + "/" + "users";
	items: UserProfile[] = [];
	orgChartData: OrgChartNode[] = [];
	orgChartRoots: OrgChartNode[] = [];
	searchQuery: string;
	selectedNode: OrgChartNode | null = null;
	selectedUser: UserProfile | null = null;
	shifts: any;
	showInactive = false;
	sites: Site[] = [];
	title: string;

	constructor(private appSettingsService: AppSettingsService, private toastService: ToastService, private router: Router, private exportLogService: ExportLogService, private protectedGridService: ProtectedGridService, private siteService: SiteService, private staffAttendanceService: StaffAttendanceService, private trainingService: TrainingService, private itemService: UserProfileService) {
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		this.shifts = GetPropertiesFromEnum(Shift);
		this.headerPrimaryButtons.push(
			{ method: "addNewItem", text: "Close" },
			// { icon: "fal fa-download", method: "generateAndDownloadStaffAttendanceCSVPopup", text: "Staff Attendance Report" },
			// { icon: "fal fa-download", method: "generateAndDownloadTrainingReportCSV", text: "Training Report" },
		);
		this.headerSecondaryButtons.push({ icon: "fal fa-ball-pile", method: "gotoGroups", text: "Groups" });
		this.title = "Organisational Chart";
		this.searchQuery = "";
		this.getItems(this.showInactive);
		this.getSites();
	}

	addNewItem() {
		this.router.navigate([`${this.itemUrl}/`]);
	}

	buildButtons() {
		this.headerTertiaryButtons = [];
		if (this.isSuperAdmin === true) {
			if (this.showInactive === true) {
				this.headerTertiaryButtons.push({ method: "showActiveProfiles", text: "Show Active Users" });
			}
			else {
				this.headerTertiaryButtons.push({ method: "showInactiveProfiles", text: "Show Inactive Users" });
			}
		}
	}

	getItems(showInactive: boolean) {
		this.gridLoading = true;
		this.items = [];
		if (showInactive === true) {
			this.itemService.getInactiveUsers()
				.subscribe(
					(res: any) => {
						this.items = res.response,
						this.gridLoading = false;
					},
					(err: any) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		} else {
			this.itemService.getActiveUsers()
				.subscribe(
					(res: any) => {
						this.items = res.response,
						this.buildChart();
						this.gridLoading = false;
					},
					(err: any) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
		this.buildButtons();
	}

	getSites() {
		this.siteService.getAll()
			.subscribe(
				(res: any) => this.sites = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	buildChart(): void {
		this.orgChartRoots = buildOrgChart(
			this.items
		);

		// Initially expand all root nodes
		// this.orgChartRoots.forEach(root => {
		// 	this.expandedNodes.add(root.user.userId);
		// });

		this.applySearch();
	}

	toggleNode(userId: number): void {
		if (this.expandedNodes.has(userId)) {
			this.expandedNodes.delete(userId);
		} else {
			this.expandedNodes.add(userId);
		}
	}

	isExpanded(userId: number): boolean {
		return this.expandedNodes.has(userId);
	}

	selectNode(node: OrgChartNode): void {
		this.selectedNode = node;
	}

	getSubordinateCount(): number {
		return 0;
		// return this.orgChartService.countSubordinates(node);
	}

	expandAll(): void {
		const expandRecursive = (node: OrgChartNode) => {
			this.expandedNodes.add(node.user.userId);
			node.children.forEach(child => expandRecursive(child));
		};

		this.orgChartRoots.forEach(root => expandRecursive(root));
	}

	collapseAll(): void {
		this.expandedNodes.clear();
		// Keep root nodes expanded
		this.orgChartRoots.forEach(root => {
			this.expandedNodes.add(root.user.userId);
		});
	}

	onSearchChange(): void {
		this.applySearch();
	}

	applySearch(): void {
		if (!this.searchQuery.trim()) {
			this.filteredRoots = this.orgChartRoots;
			return;
		}

		const query = this.searchQuery.toLowerCase();

		const filterNode = (node: OrgChartNode): OrgChartNode | null => {
			const matches =
				node.user.fullName.toLowerCase().includes(query) ||
				node.user.jobTitle.toLowerCase().includes(query);

			const filteredChildren = node.children
				.map(child => filterNode(child))
				.filter(child => child !== null) as OrgChartNode[];

			if (matches || filteredChildren.length > 0) {
				return {
					...node,
					children: filteredChildren
				};
			}

			return null;
		};

		this.filteredRoots = this.orgChartRoots
			.map(root => filterNode(root))
			.filter(root => root !== null) as OrgChartNode[];

		// Auto-expand nodes that match the search
		if (this.searchQuery.trim()) {
			const expandMatching = (node: OrgChartNode) => {
				const matches =
					node.user.fullName.toLowerCase().includes(query) ||
					node.user.jobTitle.toLowerCase().includes(query);

				if (matches) {
					this.expandedNodes.add(node.user.userId);
				}

				node.children.forEach(child => expandMatching(child));
			};

			this.filteredRoots.forEach(root => expandMatching(root));
		}
	}

	clearSearch(): void {
		this.searchQuery = "";
		this.applySearch();
	}

	getProfileImage(userProfile: UserProfile): string {
		return AvatarForUserProfile(userProfile);
	}

	hasChildren(node: OrgChartNode): boolean {
		return node.children.length > 0;
	}

}